import PropTypes from "prop-types";
import React from "react";
import { useForm } from "react-hook-form";
// @mui
import { Box, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
// hooks
import { useResponsive } from "src/hooks/use-responsive";
// component
import Scrollbar from "src/components/scrollbar/scrollbar";
import Image from "src/components/image";
import FormProvider from "src/components/hook-form";
import Button from "src/components/template/button";
//
import { BUTTON_GROUP, DIALOG, POPUP } from "src/config-global";

// ----------------------------------------------------------------------

export const XOperationalFeedback = ({ open, onCancel, onConfirm, image, title, content, cancelProps, confirmProps, cancel = "Cancel", confirm = "OK" }) => (
    <Dialog open={ open } onClose={ () => onConfirm("onClose") } sx={ { "& .MuiDialog-paper": { maxWidth: "500px", borderRadius: `${ POPUP.BASE_RADIUS_LARGE }px`, backgroundColor: "transparent" } } }>
        <Scrollbar sx={ { overflowX: "hidden", "&::-webkit-scrollbar": { display: "none" } } }>
            <Image src={ image } alt="" width={ 1 } height={ POPUP.IMAGE_HEIGHT } objectFit="cover" disabledEffect visibleByDefault />

            <Stack alignItems="center" p={ `${ POPUP.PADDING }px` } bgcolor="white" spacing={ `${ POPUP.SPACING }px` }>
                <Stack spacing={ `${ POPUP.TEXT_SPACING }px` } margin="auto" maxWidth="440px" textAlign="center">
                    <Typography variant="h2" color="text.primary">{ title }</Typography>

                    { typeof content === "string" ? (
                        <Typography variant="body1" color="text.primary">{ content }</Typography>
                    ) : (
                        <>{ content }</>
                    ) }
                </Stack>

                <Stack direction="row" alignItems="center" justifyContent="center" spacing={ `${ BUTTON_GROUP.SPACING_X }px` }>
                    { onCancel && (
                        <Button variant="outlined" layout="fixed" { ...cancelProps } onClick={ onCancel }>
                            { cancel }
                        </Button>
                    ) }

                    <Button variant="contained" layout="fixed" { ...confirmProps } onClick={ () => onConfirm("onConfirm") }>
                        { confirm }
                    </Button>
                </Stack>
            </Stack>
        </Scrollbar>
    </Dialog>
);

XOperationalFeedback.propTypes = {
    open: PropTypes.bool.isRequired,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func.isRequired,
    //
    image: PropTypes.string,
    title: PropTypes.string,
    content: PropTypes.oneOfType([ PropTypes.element, PropTypes.string ]),
    cancelProps: PropTypes.object,
    confirmProps: PropTypes.object,
    cancel: PropTypes.string,
    confirm: PropTypes.string
};

// ----------------------------------------------------------------------

export const XSystemFeedback = ({ open, onCancel, onConfirm, variant, title, content, cancel = "Cancel", confirm = "Confirm", loading, disabled, hideCancel = false, formProps }) => {
    const methods = useForm({ ...formProps });

    const { handleSubmit, formState: { isLoading } } = methods;

    const onSubmit = (values) => onConfirm(values);

    return (
        <Dialog open={ open } onClose={ onCancel } sx={ { "& .MuiDialog-paper": { maxWidth: "500px", borderRadius: `${ POPUP.BASE_RADIUS }px` } } }>
            <FormProvider methods={ methods } onSubmit={ handleSubmit(onSubmit) }>
                <XDialogContent>
                    <Stack direction="row" spacing={ `${ POPUP.SPACING_X }px` }>
                        <Box sx={ { width: `${ POPUP.ICON_WIDTH }px`, aspectRatio: 1, flexShrink: 0 } }>
                            { variant === "alert" && (
                                <Image visibleByDefault src="/assets/icon/system/ic_alert_lg.svg" alt="alert" sx={ { "& img": { objectFit: "contain" } } } disabledEffect />
                            ) }

                            { variant === "info" && (
                                <Image visibleByDefault src="/assets/icon/system/ic_info_lg.svg" alt="info" sx={ { "& img": { objectFit: "contain" } } } disabledEffect />
                            ) }

                            { variant === "success" && (
                                <Image visibleByDefault src="/assets/icon/system/ic_success_lg.svg" alt="success" sx={ { "& img": { objectFit: "contain" } } } disabledEffect />
                            ) }

                            { variant === "warning" && (
                                <Image visibleByDefault src="/assets/icon/system/ic_warning_lg.svg" alt="warning" sx={ { "& img": { objectFit: "contain" } } } disabledEffect />
                            ) }
                        </Box>

                        <Stack flex={ 1 } spacing={ `${ POPUP.TEXT_SPACING }px` } textAlign="left">
                            <Typography variant="h3" color="text.primary">{ title }</Typography>

                            { typeof content === "string" ? (
                                <Typography variant="body1" color="text.secondary">{ content }</Typography>
                            ) : (
                                <>{ content }</>
                            ) }
                        </Stack>
                    </Stack>
                </XDialogContent>

                <XDialogActions>
                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={ `${ BUTTON_GROUP.SPACING_X }px` } width={ { xs: "100%", sm: "auto" } }>
                        { !hideCancel && (
                            <Button variant="outlined" layout="adapt" color="inherit" onClick={ onCancel } sx={ { flex: { xs: 1, sm: "unset" } } }>
                                { cancel }
                            </Button>
                        ) }

                        <Button variant="contained" layout="adapt" color={ variant === "alert" ? "error" : "inherit" } loading={ loading || isLoading } type="submit" sx={ { flex: { xs: 1, sm: "unset" } } } disabled={ disabled }>
                            { confirm }
                        </Button>
                    </Stack>
                </XDialogActions>
            </FormProvider>
        </Dialog>
    );
};

XSystemFeedback.propTypes = {
    open: PropTypes.bool.isRequired,
    onCancel: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    //
    variant: PropTypes.oneOf([ "alert", "info", "success", "warning" ]).isRequired,
    //
    title: PropTypes.oneOfType([ PropTypes.element, PropTypes.string ]),
    content: PropTypes.oneOfType([ PropTypes.element, PropTypes.string ]),
    cancel: PropTypes.string,
    confirm: PropTypes.string,
    disabled: PropTypes.bool,
    hideCancel: PropTypes.bool,
    //
    loading: PropTypes.bool,
    formProps: PropTypes.object
};

// ----------------------------------------------------------------------

export const XDialog = ({ open, onClose, image, title, content, children, sx, customArea, ...props }) => {
    const isDesktop = useResponsive("up", "sm");

    return (
        <Dialog open={ open } onClose={ onClose } sx={ { "& .MuiDialog-paper": { maxWidth: "600px", borderRadius: `${ POPUP.BASE_RADIUS_LARGE }px`, overflow: "hidden" }, ...sx } } fullWidth { ...props }>
            { ( image || title || content ) && (
                <DialogTitle id="dialog-title" position="relative" sx={ { padding: `${ POPUP.PADDING }px ${ POPUP.PADDING }px ${ POPUP.PADDING_B }px ${ POPUP.PADDING }px`, backgroundColor: "background.gray" } }>
                    <Stack spacing="4px">
                        { typeof title !== "string" ? title : <Typography variant="h2" color="text.primary">{ title }</Typography> }
                        { typeof content !== "string" ? content : <Typography variant="body1" color="text.primary" maxWidth="390px">{ content }</Typography> }
                    </Stack>

                    { customArea ? (
                        <>{ customArea }</>
                    ) : (
                        <>
                            { isDesktop && image && (
                                <Stack position="absolute" bottom={ 0 } right="30px" zIndex={ 1 }>
                                    <Image src={ image } alt="" objectFit="contain" sx={ { width: "120px", height: "100px" } } />
                                </Stack>
                            ) }
                        </>
                    ) }
                </DialogTitle>
            ) }

            { children }
        </Dialog>
    );
};

XDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    //
    image: PropTypes.string,
    title: PropTypes.oneOfType([ PropTypes.node, PropTypes.string ]),
    content: PropTypes.oneOfType([ PropTypes.node, PropTypes.string ]),
    //
    customArea: PropTypes.node,
    //
    children: PropTypes.node,
    sx: PropTypes.object
};

export const XDialogContent = ({ sx, children }) => (
    <DialogContent sx={ { "&.MuiDialogContent-root": { padding: `${ POPUP.PADDING }px`, ":not(:only-child)": { marginBottom: `${ DIALOG.ACTIONS_HEIGHT }px` } }, ...sx } }>
        { children }
    </DialogContent>
);

XDialogContent.propTypes = {
    children: PropTypes.node.isRequired,
    sx: PropTypes.object
};

export const XDialogActions = ({ children, ...other }) => (
    <Stack
        position="absolute" width={ 1 } height={ `${ DIALOG.ACTIONS_HEIGHT }px` } bottom={ 0 } padding={ `${ DIALOG.ACTIONS_PADDING_Y }px ${ DIALOG.ACTIONS_PADDING_X }px` } bgcolor="white"
        direction="row" alignItems="center" justifyContent="flex-end" spacing="24px"
        borderTop="1px solid #E3E3E3" zIndex={ 1 } { ...other }
    >
        { children }
    </Stack>
);

XDialogActions.propTypes = {
    children: PropTypes.node.isRequired
};
